import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Activity, Clock, Cpu, Globe, MapPin, Waves } from "lucide-react";

import { api } from "@/api/client";
import type { Pod, Replica } from "@/api/types";
import { DrainActions, type DrainActionKind } from "@/components/DrainActions";
import { MetricTile, type Tone } from "@/components/MetricTile";
import { ReplicaPhaseCard } from "@/components/ReplicaPhaseCard";
import { Section } from "@/components/Section";
import { humanCloudLabel } from "@/workloads/tree-data";

interface Props {
  pod: Pod | null;
  replica: Replica;
  poolId: number;
}

export function PodDashboard({ pod, replica, poolId }: Props) {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<DrainActionKind | null>(null);
  // Live counters for this single replica. We piggyback on the pool-
  // scoped endpoint so we share the React-Query cache with the pool's
  // Replicas tab.
  const runtime = useQuery<Record<number, ReplicaRuntime>>({
    queryKey: ["pool", poolId, "replica-runtime"],
    queryFn: () =>
      api.get<Record<number, ReplicaRuntime>>(
        `/pools/${poolId}/replica-runtime`,
      ),
    staleTime: 4_000,
    refetchInterval: 5_000,
  });
  const rt = runtime.data?.[replica.id];

  const action = useMutation({
    mutationFn: (kind: DrainActionKind) =>
      api.post<unknown>(`/replicas/${replica.id}/action?action=${kind}`),
    onMutate: (kind) => setPending(kind),
    onSettled: () => setPending(null),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["replicas", "all"] });
      queryClient.invalidateQueries({ queryKey: ["replicas"] });
    },
  });
  const tone: Tone =
    replica.status === "ready"
      ? "ok"
      : replica.status === "failed" || replica.status === "not_ready"
        ? "danger"
        : "warn";

  const uptime = useMemo(() => {
    const startedAt = pod?.started_at ?? replica.started_at;
    if (!startedAt) return "—";
    const start = new Date(startedAt).getTime();
    const end = pod?.ended_at
      ? new Date(pod.ended_at).getTime()
      : replica.ended_at
        ? new Date(replica.ended_at).getTime()
        : Date.now();
    const seconds = Math.max(0, Math.floor((end - start) / 1000));
    return formatUptime(seconds);
  }, [pod, replica]);

  return (
    <>
      <ReplicaPhaseCard replica={replica} />
      <div className="metric-grid">
        <MetricTile
          icon={<Activity />}
          label="Status"
          value={replica.status}
          tone={tone}
          hint={`replica#${replica.id} · cluster ${replica.sky_cluster_name ?? "—"}`}
        />
        <MetricTile
          icon={<Clock />}
          label="Uptime"
          value={uptime}
          hint={
            pod?.started_at
              ? new Date(pod.started_at).toLocaleString()
              : replica.started_at
                ? new Date(replica.started_at).toLocaleString()
                : "not started"
          }
        />
        <MetricTile
          icon={<Waves />}
          label="Active WS"
          value={rt?.active_websockets ?? "—"}
          hint={
            rt
              ? `${rt.in_flight_short_lived} short-lived in-flight`
              : "no Prom sample yet"
          }
          tone={
            rt && rt.active_websockets > 0
              ? "warn"
              : rt
                ? "ok"
                : "neutral"
          }
        />
        <MetricTile
          icon={<Globe />}
          label="External"
          value={pod?.external_ip ?? replica.external_ip ?? "—"}
          hint={
            replica.external_port ? `port ${replica.external_port}` : "no port"
          }
        />
        <MetricTile
          icon={<Cpu />}
          label="Internal"
          value={pod?.internal_ip ?? "—"}
          hint={pod ? `${pod.node_role}#${pod.node_index}` : "single-node"}
        />
        <MetricTile
          icon={<MapPin />}
          label="Where"
          value={replica.cloud ? humanCloudLabel(replica.cloud) : "—"}
          hint={
            replica.region
              ? replica.instance_type
                ? `${replica.region} · ${replica.instance_type}`
                : replica.region
              : replica.instance_type ?? "not provisioned yet"
          }
        />
      </div>

      <Section title="Replica actions" icon={<Activity />}>
        <p className="muted" style={{ fontSize: 12, margin: "0 0 12px" }}>
          <code>drain WS-only</code> stops accepting new WebSocket
          upgrades but keeps short-lived requests flowing — use it on
          the highest-WS-count replica when scaling down. <code>drain</code>{" "}
          rejects all new traffic and waits for in-flight to finish.
          <code>down</code> tears the replica down immediately (no
          graceful drain).
        </p>
        <DrainActions
          status={replica.status}
          pendingKind={pending}
          onAction={(kind) => action.mutate(kind)}
        />
      </Section>
    </>
  );
}

interface ReplicaRuntime {
  active_websockets: number;
  in_flight_short_lived: number;
}

function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86_400);
  const hrs = Math.floor((seconds % 86_400) / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}d ${hrs}h`;
  if (hrs > 0) return `${hrs}h ${mins}m`;
  return `${mins}m`;
}
